import React from "react";
import { Sparkles, HeartHandshake, Flame, MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PROGRAM } from "@/lib/data";

const CARDS = [
  {
    icon: Sparkles,
    title: "Live Evidential Readings",
    text: "Susan delivers messages to audience members as loved ones come through — with names, memories, and the details that leave no doubt.",
  },
  {
    icon: HeartHandshake,
    title: "Messages of Healing & Hope",
    text: "Even readings meant for strangers land where you need them. This is an evening designed to soften the ache of loss.",
  },
  {
    icon: Flame,
    title: "Candlelight Remembrance",
    text: "In the heart of the show, the house glows as we honor the people we carry with us — a candlelit ritual you will never forget.",
  },
  {
    icon: MessageCircle,
    title: "Wisdom & Open Q&A",
    text: "Ask Susan anything about the other side, grief, and living an infinite life — answered with 30 years of truth.",
  },
];

const Experience = () => (
  <section id="experience" className="relative py-16 sm:py-24 bg-aurora">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="The Evening"
        title={
          <>
            An hour of candlelight in a season of <em className="text-gold-gradient italic">missing them</em>
          </>
        }
        sub="The turn of the year can amplify absence. This evening exists to turn that ache into connection — held inside the gilded warmth of the historic Saban Theatre."
      />

      <Reveal delay={0.15} className="max-w-3xl mx-auto text-center mt-14">
        <blockquote className="font-display italic text-2xl sm:text-3xl leading-snug text-[#FDFBF7]">
          "They're only a heartbeat away."
        </blockquote>
        <p className="eyebrow mt-5">— Susan Grau</p>
      </Reveal>

      <div className="grid sm:grid-cols-2 gap-5 mt-16">
        {CARDS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <div className="card-lift h-full glass rounded-3xl p-8 flex gap-6 items-start">
              <div className="shrink-0 w-12 h-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center bg-[#D4AF37]/10">
                <c.icon size={20} className="text-[#F3D068]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display text-2xl text-[#FDFBF7]">{c.title}</h3>
                <p className="mt-3 text-[#9CA3AF] font-light leading-relaxed">{c.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Program timeline — full width, centered */}
      <div className="mt-16 max-w-2xl mx-auto">
        <div>
          <Reveal>
            <p className="eyebrow">Order of the Evening</p>
            <h3 className="font-display text-3xl sm:text-4xl mt-4 text-[#FDFBF7]">
              Doors at 6 · Candlelight by 8:35
            </h3>
          </Reveal>
          <div className="mt-8 relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-[#D4AF37]/60 via-[#7C74F2]/40 to-transparent" />
            {PROGRAM.map((p, i) => (
              <Reveal key={p.time} delay={i * 0.07} className="relative pl-10 pb-7">
                <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border border-[#D4AF37] bg-[#0B1021] flex items-center justify-center">
                  <span className="w-[5px] h-[5px] rounded-full bg-[#F3D068]" />
                </span>
                <p className="font-cinzel text-xs tracking-[0.25em] text-[#F3D068]">{p.time}</p>
                <h4 className="font-display text-xl mt-1.5 text-[#FDFBF7]">{p.title}</h4>
                <p className="text-sm text-[#9CA3AF] font-light mt-1.5 leading-relaxed">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>

      </div>
    </div>
  </section>
);

export default Experience;
